import { useHasClinicPermission } from '@/hooks/use-clinic-permission';
import { ATTACHMENT_MAX_SIZE_BYTES, ATTACHMENT_MIME_TYPES, deleteAttachment, finalizeAttachment, getAttachmentDownloadUrl, getAttachmentUploadUrl, isSupportedAttachmentMime, listStudyAttachments, uploadToSignedUrl, } from '@/lib/api/attachments';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// Gates the upload/delete affordances (see useHasClinicPermission for the
// UI-only nature of the check).
export function useCanManageAttachments(clinicId) {
    return useHasClinicPermission(clinicId, 'manage_attachments');
}
export function useStudyAttachments(studyId) {
    return useQuery({
        queryKey: ['attachments', studyId],
        queryFn: () => listStudyAttachments(studyId),
        enabled: !!studyId,
    });
}
export function useDeleteAttachment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ attachmentId }) => deleteAttachment(attachmentId),
        onSuccess: (_, { studyId }) => {
            queryClient.invalidateQueries({ queryKey: ['attachments', studyId] });
        },
    });
}
// Server issues a 10-minute signed URL. Refresh proactively at 8 min so a
// preview that stays mounted (e.g. user left a PDF iframe open) doesn't tip
// over into an expired signature. staleTime alone is passive — it only
// reconsiders the cache on next mount/focus and would not save a long-lived
// preview, so we also set refetchInterval.
const ATTACHMENT_URL_REFRESH_MS = 8 * 60 * 1000;
export function useAttachmentDownloadUrl(attachmentId) {
    return useQuery({
        queryKey: ['attachment-download-url', attachmentId],
        queryFn: () => getAttachmentDownloadUrl(attachmentId),
        enabled: !!attachmentId,
        staleTime: ATTACHMENT_URL_REFRESH_MS,
        refetchInterval: ATTACHMENT_URL_REFRESH_MS,
        refetchOnWindowFocus: true,
    });
}
export function validateAttachmentFile(file) {
    if (!isSupportedAttachmentMime(file.type)) {
        const allowed = Object.keys(ATTACHMENT_MIME_TYPES).join(', ');
        return `Unsupported file type "${file.type || 'unknown'}". Allowed: ${allowed}.`;
    }
    if (file.size <= 0)
        return 'File is empty.';
    if (file.size > ATTACHMENT_MAX_SIZE_BYTES) {
        return `File exceeds the ${ATTACHMENT_MAX_SIZE_BYTES / (1024 * 1024)} MB limit.`;
    }
    return null;
}
export function useUploadAttachment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ studyId, file, onProgress, signal }) => {
            const validationError = validateAttachmentFile(file);
            if (validationError)
                throw new Error(validationError);
            const { attachmentId, uploadUrl, objectPath } = await getAttachmentUploadUrl({
                studyId,
                fileName: file.name,
                sizeBytes: file.size,
                mimeType: file.type,
            });
            await uploadToSignedUrl(uploadUrl, file, { signal, onProgress });
            return finalizeAttachment({
                attachmentId,
                studyId,
                objectPath,
                fileName: file.name,
                mimeType: file.type,
            });
        },
        onSuccess: (_, { studyId }) => {
            queryClient.invalidateQueries({ queryKey: ['attachments', studyId] });
        },
    });
}
